import React, { useEffect, useState, useCallback } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import {
  ShieldCheck, ShieldOff, Lock, Unlock, CalendarDays, Timer, RefreshCw,
} from 'lucide-react';

export default function OwnerControls() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(null);
  const [form, setForm] = useState({
    enabled: false, start_date: '', duration_days: 60, kill_switch: false,
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axiosInstance.get('/pilot/config');
      setStatus(data.status);
      setForm({
        enabled: !!data.settings.enabled,
        start_date: data.settings.start_date || '',
        duration_days: data.settings.duration_days || 60,
        kill_switch: !!data.settings.kill_switch,
      });
    } catch (e) {
      toast.error(e.response?.status === 403 ? 'هذه الصفحة للمالك (Admin) فقط' : 'فشل تحميل الإعدادات');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async (payload) => {
    setSaving(true);
    try {
      const { data } = await axiosInstance.put('/pilot/config', payload);
      toast.success(data.message_ar);
      setStatus(data.status);
      setForm({
        enabled: !!data.settings.enabled,
        start_date: data.settings.start_date || '',
        duration_days: data.settings.duration_days || 60,
        kill_switch: !!data.settings.kill_switch,
      });
    } catch (e) {
      toast.error(e.response?.data?.detail?.message_ar || 'فشل الحفظ');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center gap-2 text-gray-500" dir="rtl">
        <RefreshCw className="w-4 h-4 animate-spin" /> جارٍ التحميل…
      </div>
    );
  }

  const locked = status?.is_expired;

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-6" dir="rtl" data-testid="owner-controls-page">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <ShieldCheck className="w-6 h-6 text-indigo-600" />
          شاشة تحكم المالك
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          إدارة قفل الفترة التجريبية بنقرة واحدة — أنت وحدك من يتحكم بها.
        </p>
      </div>

      {/* Current status */}
      <div
        data-testid="owner-lock-status"
        className={`rounded-xl border p-4 flex items-center gap-3 ${
          locked ? 'bg-red-50 border-red-300' : 'bg-emerald-50 border-emerald-300'
        }`}
      >
        {locked ? <Lock className="w-6 h-6 text-red-600" /> : <Unlock className="w-6 h-6 text-emerald-600" />}
        <div>
          <p className={`font-semibold ${locked ? 'text-red-700' : 'text-emerald-700'}`}>
            {locked ? 'النظام محجوب حالياً (القفل مفعّل)' : 'النظام مفتوح — لا يوجد أي قفل نشط'}
          </p>
          <p className="text-xs text-gray-600 mt-0.5">
            {status?.expires_at
              ? `تاريخ الانتهاء: ${status.expires_at} — المتبقي: ${status.days_remaining} يوم`
              : 'لا يوجد تاريخ انتهاء — النظام يعمل بلا حدود زمنية'}
          </p>
        </div>
      </div>

      {/* One-click unlock */}
      {(form.enabled || locked) && (
        <button
          data-testid="owner-unlock-all-btn"
          onClick={() => save({ enabled: false })}
          disabled={saving}
          className="w-full py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
        >
          <ShieldOff className="w-5 h-5" />
          إيقاف كل الأقفال فوراً (نقرة واحدة)
        </button>
      )}

      {/* Pilot period management */}
      <div className="rounded-xl border border-gray-200 bg-white p-5 space-y-4">
        <h2 className="font-semibold text-gray-800 flex items-center gap-2">
          <Timer className="w-4 h-4 text-indigo-600" />
          إعداد فترة تجريبية (اختياري — لعرض النظام على عميل مثلاً)
        </h2>
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            data-testid="owner-pilot-enabled"
            checked={form.enabled}
            onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
            className="w-4 h-4"
          />
          تفعيل قفل الفترة التجريبية
        </label>
        {form.enabled && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-gray-500 flex items-center gap-1 mb-1">
                <CalendarDays className="w-3 h-3" /> تاريخ البدء
              </label>
              <input
                type="date"
                data-testid="owner-pilot-start"
                value={form.start_date}
                onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                className="form-input w-full"
              />
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">المدة (أيام)</label>
              <input
                type="number"
                min="1"
                data-testid="owner-pilot-duration"
                value={form.duration_days}
                onChange={(e) => setForm({ ...form, duration_days: parseInt(e.target.value) || 60 })}
                className="form-input w-full"
              />
            </div>
            <label className="flex items-center gap-2 text-sm text-red-700 sm:col-span-2">
              <input
                type="checkbox"
                data-testid="owner-kill-switch"
                checked={form.kill_switch}
                onChange={(e) => setForm({ ...form, kill_switch: e.target.checked })}
                className="w-4 h-4"
              />
              مفتاح الإيقاف الفوري (يحجب النظام فوراً بغض النظر عن التاريخ)
            </label>
          </div>
        )}
        <button
          data-testid="owner-save-btn"
          onClick={() => save(form)}
          disabled={saving}
          className="btn-primary px-6 py-2 disabled:opacity-50"
        >
          {saving ? 'جارٍ الحفظ…' : 'حفظ الإعدادات'}
        </button>
        <p className="text-[11px] text-gray-500 leading-relaxed">
          ملاحظة: حتى لو انتهت الفترة وانحجب النظام، تبقى هذه الشاشة وتسجيل الدخول
          متاحين لك دائماً لفك القفل بنقرة واحدة. الإعدادات محفوظة في قاعدة البيانات
          وتنطبق على الموقع الفعلي مباشرة بعد النشر — بدون أي تعديل تقني.
        </p>
      </div>
    </div>
  );
}
